import { useState, useMemo } from "react";
import { sqlRead } from "../utils/sqlClient";
import { factPayload, factsByCategory, todayIso, unresolvedPlaceholders } from "../utils/promptFacts";

const th = { padding: "6px 8px", fontWeight: 600, borderBottom: "1px solid var(--cs-border)" };
const td = { padding: "7px 8px", borderBottom: "1px solid var(--cs-border)", verticalAlign: "top" };

const CATEGORY_LABEL = { company: "Company", insurance: "Insurance (from the current COI)" };

// Admin-editable values the prompts reference as {{fact.key}}. Edit once here;
// every prompt that uses the fact picks it up on the next analysis run.
export default function CompanyFactsPanel({ facts, prompts, userEmail, onChanged, onError }) {
  const [editKey, setEditKey] = useState(null);
  const [draft, setDraft] = useState("");
  const [verified, setVerified] = useState(false);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(true);

  const groups = useMemo(() => factsByCategory(facts), [facts]);
  const unresolved = useMemo(() => unresolvedPlaceholders(prompts, facts), [prompts, facts]);

  const startEdit = (f) => { setEditKey(f.FactKey); setDraft(f.FactValue); setVerified(false); };

  const save = async (f) => {
    if (!draft.trim() || saving) return;
    setSaving(true);
    try {
      const payload = factPayload({
        key: f.FactKey,
        value: draft.trim(),
        verifiedOn: verified ? todayIso() : undefined,
        by: userEmail,
      });
      const json = JSON.stringify(payload).replace(/'/g, "''");
      await sqlRead(`EXEC sp_SavePromptFact @payload = N'${json}'`);
      setEditKey(null);
      onChanged();
    } catch (e) {
      onError(e.message || String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ border: "1px solid var(--cs-border)", borderRadius: "var(--radius)", marginBottom: 22, background: "var(--surface)" }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{ width: "100%", textAlign: "left", background: "none", border: "none", padding: "11px 14px", cursor: "pointer", display: "flex", gap: 8, alignItems: "center" }}
      >
        <span style={{ fontSize: 13, fontWeight: 700, color: "var(--text)" }}>Company Facts</span>
        <span style={{ fontSize: 11, color: "var(--text3)" }}>
          Names and insurance figures the prompts reference. Update a value here once — every prompt using it follows.
        </span>
        <span style={{ marginLeft: "auto", fontSize: 11, color: "var(--text3)" }}>{open ? "▾" : "▸"}</span>
      </button>

      {open && (
        <div style={{ padding: "0 14px 14px" }}>
          {unresolved.length > 0 && (
            <div style={{ border: "1px solid #fde68a", background: "var(--tint-amber)", color: "#92400e", borderRadius: "var(--radius)", padding: "8px 11px", fontSize: 11.5, marginBottom: 10 }}>
              Prompt text references {unresolved.length === 1 ? "a fact" : "facts"} that {unresolved.length === 1 ? "isn't" : "aren't"} defined, so the AI will see the raw placeholder:{" "}
              {unresolved.map(u => `{{${u.token}}} (in ${u.promptKeys.join(", ")})`).join("; ")}
            </div>
          )}

          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ textAlign: "left", color: "var(--text3)", fontSize: 10.5, textTransform: "uppercase", letterSpacing: 0.4 }}>
                <th style={th}>Fact</th>
                <th style={{ ...th, width: "50%" }}>Current value</th>
                <th style={{ ...th, width: 120 }}>Source check</th>
                <th style={{ ...th, width: 44, textAlign: "right" }}>Used</th>
                <th style={{ ...th, width: 44 }} />
              </tr>
            </thead>
            {groups.map(([cat, rows]) => (
              <tbody key={cat}>
                <tr>
                  <td colSpan={5} style={{ background: "var(--surface2)", padding: "5px 8px", fontSize: 10.5, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: 0.4 }}>
                    {CATEGORY_LABEL[cat] || cat}
                  </td>
                </tr>
                {rows.map(f => (
                  <FactRow key={f.FactKey} f={f} editing={editKey === f.FactKey}
                    draft={draft} setDraft={setDraft} verified={verified} setVerified={setVerified}
                    saving={saving} onEdit={() => startEdit(f)} onCancel={() => setEditKey(null)} onSave={() => save(f)} />
                ))}
              </tbody>
            ))}
          </table>
        </div>
      )}
    </div>
  );
}


function FactRow({ f, editing, draft, setDraft, verified, setVerified, saving, onEdit, onCancel, onSave }) {
  return (
    <>
      <tr>
        <td style={td}>
          <div style={{ fontWeight: 600 }}>{f.Label}</div>
          <code style={{ fontSize: 10, color: "var(--text3)" }}>{`{{${f.FactKey}}}`}</code>
        </td>
        <td style={{ ...td, color: "var(--text2)", lineHeight: 1.45 }}>{f.FactValue}</td>
        <td style={td}>
          {f.VerifiedOn ? (
            <span style={{ fontSize: 11, fontWeight: 600, color: "#15803d" }}>✓ {String(f.VerifiedOn).slice(0, 10)}</span>
          ) : (
            <span style={{ fontSize: 11, fontWeight: 600, color: "#92400e" }}>Not verified</span>
          )}
        </td>
        <td style={{ ...td, textAlign: "right", color: "var(--text3)" }} title="Active prompt sections that use this fact">{f.UsedByCount}</td>
        <td style={td}>
          {!editing && (
            <button onClick={onEdit} style={{ fontSize: 11, color: "var(--cs-blue)", background: "none", border: "none", cursor: "pointer" }}>Edit</button>
          )}
        </td>
      </tr>
      {editing && (
        <tr>
          <td colSpan={5} style={{ ...td, background: "var(--surface2)" }}>
            <textarea
              value={draft}
              onChange={e => setDraft(e.target.value)}
              rows={2}
              style={{ width: "100%", boxSizing: "border-box", border: "1px solid var(--cs-border)", borderRadius: "var(--radius)", padding: "7px 9px", fontSize: 12, resize: "vertical" }}
            />
            <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 11, color: "var(--text2)", margin: "6px 0" }}>
              <input type="checkbox" checked={verified} onChange={e => setVerified(e.target.checked)} />
              I checked this value against the source document (current COI / Articles) — stamp today's date
            </label>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                onClick={onSave}
                disabled={!draft.trim() || saving}
                style={{ height: 28, padding: "0 14px", fontSize: 12, fontWeight: 600, color: "#fff", background: draft.trim() && !saving ? "var(--cs-navy)" : "#cbd5e1", border: "none", borderRadius: "var(--radius)", cursor: "pointer" }}
              >
                {saving ? "Saving…" : "Save"}
              </button>
              <button
                onClick={onCancel}
                style={{ height: 28, padding: "0 12px", fontSize: 12, color: "var(--text2)", background: "none", border: "1px solid var(--cs-border)", borderRadius: "var(--radius)", cursor: "pointer" }}
              >
                Cancel
              </button>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
